/**
 * Returns whether a play() rejection is expected during lifecycle DOM churn.
 *
 * @param {Object} error - The play() rejection error.
 * @returns {boolean}
 */
export function isExpectedPlayInterruptionError(error) {
    const message = error?.message || '';

    return error?.name === 'AbortError'
        || message.includes('play() request was interrupted')
        || message.includes('media was removed from the document');
}

/**
 * Returns whether a play() rejection was caused by browser autoplay policy.
 *
 * @param {Object} error - The play() rejection error.
 * @returns {boolean}
 */
export function isAutoplayPolicyError(error) {
    const message = error?.message || '';

    return error?.name === 'NotAllowedError'
        || /not allowed|autoplay|user gesture|denied permission/i.test(message);
}

/**
 * Calls play() safely and classifies expected interruption/autoplay policy errors.
 *
 * @param {HTMLMediaElement} element - Media element to play.
 * @param {Object} handlers - Optional callbacks for error classes.
 * @param {boolean} handlers.allowDetached - Allow play() on detached DOM media elements.
 * @param {Function} handlers.onExpectedInterruption - Called for expected interruptions.
 * @param {Function} handlers.onAutoplayBlocked - Called for autoplay policy rejections.
 * @param {Function} handlers.onError - Called for unexpected errors.
 * @returns {Promise|void}
 */
export function safePlayMediaElement(element, handlers = {}) {
    if (!element || typeof element.play !== 'function') {
        return;
    }

    // Keep detached-DOM protection for regular UI media elements, but avoid
    // blocking non-DOM audio implementations (e.g. native sound wrappers)
    // and explicitly allowed detached capture pipelines.
    if (!handlers.allowDetached
        && typeof element.isConnected === 'boolean'
        && element.isConnected === false) {
        return;
    }

    const playPromise = element.play();

    if (playPromise === undefined) {
        return;
    }

    return playPromise.catch(error => {
        if (isExpectedPlayInterruptionError(error)) {
            handlers.onExpectedInterruption?.(error);

            return;
        }

        if (isAutoplayPolicyError(error)) {
            handlers.onAutoplayBlocked?.(error);

            return;
        }

        handlers.onError?.(error);
    });
}
